import React, { useState, useCallback, useRef, useEffect } from "react";
import styled from "styled-components";
import tw from "tailwind.macro";
import useOnClickOutside from "utils/hooks/useOnClickOutside";
import CloseIcon from "components/icons/Close";
import GridIcon from "components/icons/Grid";
import ListIcon from "components/icons/List";
import CarouselImage from "./CarouselImage";
import { Gallery, MediaCarousel } from "../";
import useBreakpoint from "utils/hooks/useBreakpoint";
import { withRouter } from "react-router-dom";
import queryString from "query-string";
import { StyledModal } from "../";

const getItemWidth = (breakpoint) => {
  switch (breakpoint) {
    case "2xl":
      return 1000;
    case "xl":
      return 900;
    case "lg":
      return 700;
    default:
      return 100;
  }
};

const ModalContent = styled.div`
  ${tw`flex flex-col items-center justify-between bg-white p-0`}
  max-width: 1540px;
  max-height: 840px;
  width: 95vw;
  height: 95vh;
  min-width: 90vw;
  min-height: 90vh;
`;

const Header = styled.div`
  ${tw`text-mattammy-gray w-full font-trade-gothic-20 uppercase flex justify-between items-center
    border-b border-border-light-blue md:px-30-mattamy h-60-mattamy
  `}
  height: 3.75rem;
  font-size: 1.375rem;
  padding-left: 1.25rem;
  padding-right: 1.25rem;
`;

const Content = styled.div`
  ${tw`w-full flex items-center`}
  height: calc(100% - 3.75rem);
`;

const ContentMediaCarousel = styled.div`
  ${tw`w-9/12 relative hidden lg:block`}
  height: 95%;
`;

const FiltersContainer = styled.div.attrs({
  className: "bg-action-blue absolute bottom-0 w-full flex z-20",
})`
  height: 3.125rem;
`;

const Filter = styled.div.attrs(({ active }) => ({
  className: `flex items-center justify-center flex-1 text-white font-trade-gothic-20 font-semibold
    ${active ? "bg-mattamy-blue-dark" : ""}`,
}))`
  ${({ active }) =>
    active &&
    `
    & div:after {
      content: "";
      background-color: white;
      position: absolute;
      left: 0;
      right: 0;
      height: 0.188rem;
      bottom: -0.188rem;
    }`}
`;

const getGallery = (media, filter) =>
  !filter ? media : media.filter((e) => e.type === filter);

const getSlides = (items) =>
  items.map((item) => <CarouselImage key={item.thumbnail} {...item} />);

const GalleryModal = (props) => {
  const [filter, setFilter] = useState();
  const [carouselPosition, setCarouselPosition] = useState(0);
  const [galleryType, setGalleryType] = useState("gallery");

  const gallery = getGallery(props.media, filter);
  const slides = getSlides(gallery);

  useEffect(() => {
    const params = queryString.parse(props.location.search);
    setCarouselPosition(gallery.findIndex((i) => i.id === params.gelleryImage));
  }, [props.location]);

  const setUrlParam = (item) => {
    const params = queryString.parse(props.location.search);
    if (params.galleryImage) {
      props.history.push({
        pathname: props.history.pathname,
        search: "?" + new URLSearchParams({ gelleryImage: item.id }).toString(),
      });
    }
  };

  const handleNextClick = useCallback(() => {
    if (carouselPosition < slides.length - 1) {
      setCarouselPosition(carouselPosition + 1);
      setUrlParam(gallery[carouselPosition + 1]);
    }
  }, [carouselPosition, slides.length]);

  const handlePrevClick = useCallback(() => {
    if (carouselPosition > 0) {
      setCarouselPosition(carouselPosition - 1);
      setUrlParam(gallery[carouselPosition - 1]);
    }
  }, [carouselPosition]);

  const handleChange = useCallback(
    (value) => {
      if (value >= 0 && value < slides.length) {
        setCarouselPosition(value);
        setUrlParam(gallery[value]);
      }
    },
    [slides.length]
  );

  const handleSelectImage = useCallback(
    (item) => {
      const index = gallery.findIndex((e) => e.id === item.id);
      if (index !== -1) {
        setCarouselPosition(index);
        setUrlParam(gallery[index]);
      }
    },
    [gallery]
  );

  const toggleGalleryType = useCallback(() => {
    setGalleryType(galleryType === "gallery" ? "list" : "gallery");
  }, [galleryType]);

  useEffect(() => {
    setCarouselPosition(0);
    setUrlParam(gallery[0]);
  }, [filter]);

  const handleFilterChange = (value) => {
    setFilter(value);
  };

  const modalRef = useRef();
  useOnClickOutside(modalRef, props.onClose);

  const breakpoint = useBreakpoint(
    typeof window !== "undefined" && window.innerWidth
  );

  return (
    <StyledModal isOpen={props.isOpen}>
      <ModalContent>
        <div
          className="w-full h-full flex flex-col items-center"
          ref={modalRef}
          aria-modal="true"
          role="dialog"
        >
          <Header>
            <button onClick={toggleGalleryType} className="lg:hidden w-4">
              {galleryType === "gallery" ? (
                <ListIcon className="text-action-blue w-6 h-6" />
              ) : (
                <GridIcon className="text-action-blue w-6 h-6" />
              )}
            </button>
            <span className="mt-2">GALLERY</span>
            <button onClick={props.onClose}>
              <CloseIcon className="text-action-blue w-8 h-8" />
            </button>
          </Header>
          <Content>
            <ContentMediaCarousel>
              {gallery.length > 0 ? (
                <MediaCarousel
                  value={carouselPosition}
                  slides={slides}
                  centered
                  slidesPerPage={1}
                  itemWidth={getItemWidth(breakpoint)}
                  minDraggableOffset={10}
                  onChange={handleChange}
                  onPrevClick={handlePrevClick}
                  onNextClick={handleNextClick}
                />
              ) : (
                <div className="flex w-full h-full items-center justify-center font-franklin text-mattamy-gray text-md italic">
                  Content is not available.
                </div>
              )}
            </ContentMediaCarousel>
            <div className="w-full lg:w-3/12 border-l border-border-light-blue h-full flex flex-col justift-between h-full relative">
              <div
                className="flex-grow mb-8 overflow-y-scroll"
                onClick={() => setFilter()}
              >
                {gallery.length > 0 ? (
                  <Gallery
                    media={gallery}
                    columnsLg={3}
                    columnsSm={galleryType === "gallery" ? 3 : 1}
                    thumbnailItems={0}
                    gridGap={"1rem"}
                    className="p-4"
                    onItemClick={handleSelectImage}
                    selectedImage={carouselPosition}
                    breakpoint={breakpoint}
                  />
                ) : (
                  <div className="flex w-full h-full items-center justify-center font-franklin text-mattamy-gray text-xs">
                    Content is not available.
                  </div>
                )}
              </div>
              <FiltersContainer>
                <Filter
                  onClick={() => handleFilterChange("video")}
                  active={filter === "video"}
                >
                  <div className="relative">VIDEOS</div>
                </Filter>
                <Filter
                  onClick={() => handleFilterChange("image")}
                  active={filter === "image"}
                >
                  <div className="relative">PHOTOS</div>
                </Filter>
                <Filter
                  onClick={() => handleFilterChange("tour")}
                  active={filter === "tour"}
                >
                  <div className="relative">TOURS</div>
                </Filter>
              </FiltersContainer>
            </div>
          </Content>
        </div>
      </ModalContent>
    </StyledModal>
  );
};

export default withRouter(GalleryModal);
